// hooks/integrations/synapse/useSynapseFastqSearch.ts
import { useCallback, useEffect, useRef, useState } from 'react';

import useApi from '@hooks/useApi';

import { EntityChildrenRequest, EntityChildrenResponse, SynapseChild } from '@models/integrations/synapse/Synapse';

import Endpoints from '@services/Endpoints';

import Logger from '@util/Logger';

interface FastqFile extends SynapseChild {
    path: string;
}

const isFastqFile = (fileName: string): boolean => {
    const lowerName = fileName.toLowerCase();
    return (
        lowerName.endsWith('.fastq') ||
        lowerName.endsWith('.fq') ||
        lowerName.endsWith('.fastq.gz') ||
        lowerName.endsWith('.fq.gz')
    );
};

const useSynapseFastqSearch = (projectId: string | null) => {
    const [fastqFiles, setFastqFiles] = useState<FastqFile[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [searchError, setSearchError] = useState<string>('');
    const [progress, setProgress] = useState<{ processed: number; total: number }>({ processed: 0, total: 0 });
    const [searchComplete, setSearchComplete] = useState<boolean>(false);

    // Use refs to ensure stable references
    const apiRef = useRef(useApi());
    const loggerRef = useRef(Logger.make('SynapseFastqSearch'));

    // Reset state when project ID changes
    useEffect(() => {
        if (projectId) {
            setFastqFiles([]);
            setIsSearching(false);
            setSearchError('');
            setProgress({ processed: 0, total: 0 });
            setSearchComplete(false);
        }
    }, [projectId]);

    // Helper function to fetch folder children directly using API
    const fetchFolderChildren = useCallback(
        async (folderId: string, nextPageToken?: string): Promise<EntityChildrenResponse> => {
            const url = Endpoints.integrations.synapse.folderChildren(folderId);
            const requestBody: EntityChildrenRequest = {
                parentId: folderId,
                includeTypes: ['folder', 'file'],
                sortBy: 'NAME',
                sortDirection: 'ASC',
                includeTotalChildCount: true,
                includeSumFileSizes: true,
            };

            if (nextPageToken) {
                requestBody.nextPageToken = nextPageToken;
            }

            return await apiRef.current.post(url, requestBody);
        },
        [],
    );

    const searchForFastqFiles = useCallback(async () => {
        if (!projectId) {
            setSearchError('No project ID provided');
            return;
        }

        setIsSearching(true);
        setSearchError('');
        setFastqFiles([]);
        setProgress({ processed: 0, total: 0 });
        setSearchComplete(false);

        try {
            // Queue of folders to process (with their paths)
            const folderQueue: { id: string; path: string }[] = [{ id: projectId, path: '' }];
            const processedFolders = new Set<string>();
            const foundFiles: FastqFile[] = [];

            // Process folders until queue is empty
            while (folderQueue.length > 0) {
                const { id: folderId, path } = folderQueue.shift() || { id: '', path: '' };

                // Skip if already processed
                if (processedFolders.has(folderId)) continue;
                processedFolders.add(folderId);

                try {
                    let hasMorePages = true;
                    let pageToken: string | undefined = undefined;

                    // Process all pages for this folder
                    while (hasMorePages) {
                        const response = await fetchFolderChildren(folderId, pageToken);
                        const children = response.page || [];

                        // Process children
                        for (const child of children) {
                            const childPath = path ? `${path} / ${child.name}` : child.name;

                            if (child.type === 'org.sagebionetworks.repo.model.Folder') {
                                // Add folder to queue
                                folderQueue.push({ id: child.id, path: childPath });
                            } else if (isFastqFile(child.name)) {
                                // Add FASTQ file to results
                                foundFiles.push({
                                    ...child,
                                    path: childPath,
                                });
                            }
                        }

                        // Check if there are more pages
                        if (response.nextPageToken) {
                            pageToken = response.nextPageToken;
                        } else {
                            hasMorePages = false;
                        }
                    }
                } catch (err) {
                    loggerRef.current.error(`Error fetching children for folder ${folderId}:`, err);
                    // Continue with other folders even if one fails
                }

                // Update progress
                setProgress((_prev) => ({
                    processed: processedFolders.size,
                    total: processedFolders.size + folderQueue.length,
                }));

                // Update found files periodically
                if (foundFiles.length > 0 && (processedFolders.size % 5 === 0 || folderQueue.length === 0)) {
                    setFastqFiles([...foundFiles]);
                }
            }

            setFastqFiles(foundFiles);
            setSearchComplete(true);
        } catch (err) {
            loggerRef.current.error('Error searching for FASTQ files:', err);
            setSearchError('Failed to search for FASTQ files');
        } finally {
            setIsSearching(false);
        }
    }, [projectId, fetchFolderChildren]);

    return {
        fastqFiles,
        isSearching,
        searchError,
        progress,
        searchComplete,
        searchForFastqFiles,
    };
};

export default useSynapseFastqSearch;
